import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, Link, useNavigate } from "react-router-dom";
import { Paper, Box, Stack, Typography, Button, Tooltip, IconButton, useTheme, CircularProgress, Grid, Chip } from "@mui/material";
import { ZoomIn as ViewIcon } from "@mui/icons-material";
import moment from "moment";

import Header from "component/Header";
import DataGridTable from "component/DataGridTable";
import TableSkeleton from "component/Loaders/TableSkeleton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import { getAllUsers } from "features/appUsers/appUsers.slice";
import { readAsyncStorageValues, getServiceTicketsByUserId } from "features/service/service.slice";
import AddEditUser from "../AddEditUser";
import RegistrationNotifyDialog from "../components/RegistrationNotifyDialog";
import { Links } from "utils/pageLinkNames.js";

const parseInactiveReasonFromInfo = (info) => {
  if (!info) return "";
  try {
    const infoMap = typeof info === "string" ? JSON.parse(info) : info;
    return infoMap?.inactiveReason ? String(infoMap.inactiveReason).trim() : "";
  } catch {
    return "";
  }
};

const parseResponsibleForFacilitiesTicketsFromInfo = (info) => {
  if (!info) return false;
  try {
    const infoMap = typeof info === "string" ? JSON.parse(info) : info;
    return !!infoMap?.responsible_for_facilities_tickets;
  } catch {
    return false;
  }
};

const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";
  // Remove non-numeric characters
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  // Assuming the country code is the first two digits
  const countryCode = digitsOnly.slice(0, 2);
  const localNumber = digitsOnly.slice(2);

  if (localNumber.length === 10) {
    return `+${countryCode} ${localNumber.slice(0, 3)} ${localNumber.slice(3, 6)} ${localNumber.slice(6, 10)}`;
  } else if (localNumber.length === 8) {
    return `+${countryCode} ${localNumber.slice(0, 4)} ${localNumber.slice(4, 8)}`;
  } else {
    return phoneNumber;
  }
};

const User = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const theme = useTheme();

  const [initialFormData, setInitialFormData] = useState({
    name: "",
    email: "",
    mobileNumber: "",
    department: "",
    userRole: "",
    userName: "",
    password: "",
    confirmPassword: "",
  });
  const routeNotifyClearedRef = useRef(false);

  const [enableEditing, setEnableEditing] = useState(() => {
    if (location.state?.accountCreatedNotify) return false;
    if (!id) return true;
    return Boolean(location.state?.enableEditing);
  });
  const [accountCreatedNotify, setAccountCreatedNotify] = useState(() => location.state?.accountCreatedNotify ?? null);

  // Prefer route notify on first paint after create→edit so we never flash the form with a new id.
  const notifyPayload = accountCreatedNotify ?? location.state?.accountCreatedNotify;
  const showEditForm = !id || (enableEditing && !notifyPayload);
  const [serviceTickets, setServiceTickets] = useState([]);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [inactiveReasonExpanded, setInactiveReasonExpanded] = useState(false);

  const INACTIVE_REASON_PREVIEW_LENGTH = 40;

  const { appConstants, serviceTicketsByUserId, serviceTicketsByStatusList, isLoading } = useSelector((state) => state.service);
  const { allUsers } = useSelector((state) => state.appUsers);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (!id || !serviceTicketsByUserId) return;
    setServiceTickets(Array.isArray(serviceTicketsByUserId.data) ? serviceTicketsByUserId.data : []);
  }, [id, serviceTicketsByUserId]);

  useEffect(() => {
    dispatch(readAsyncStorageValues());
    dispatch(getAllUsers());
  }, [dispatch]);

  useEffect(() => {
    routeNotifyClearedRef.current = false;
  }, [id]);

  // Apply post-create notify before paint when add→edit reuses this component instance.
  useLayoutEffect(() => {
    const notify = location.state?.accountCreatedNotify;
    if (!notify) return;
    setAccountCreatedNotify(notify);
    setEnableEditing(false);
  }, [location.state?.accountCreatedNotify]);

  // Strip notify payload from history once (avoids reload reopening dialog).
  useEffect(() => {
    if (routeNotifyClearedRef.current || !location.state?.accountCreatedNotify) return;
    routeNotifyClearedRef.current = true;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (location.state?.accountCreatedNotify || notifyPayload) return;
    if (!id) {
      setEnableEditing(true);
    } else if (location.state?.enableEditing) {
      setEnableEditing(true);
    } else {
      setEnableEditing(false);
    }
  }, [id, location.state?.enableEditing, location.state?.accountCreatedNotify, notifyPayload]);

  useEffect(() => {
    const payload = {
      userId: id,
    };
    if (selectedStartDate) payload.timeStamp = moment(selectedStartDate).format("YYYY-MM-DD");
    if (id) dispatch(getServiceTicketsByUserId(payload));
  }, [dispatch, id, selectedStartDate]);

  useEffect(() => {
    if (!id || !allUsers.length || !Object.keys(appConstants).length) return;
    const info = allUsers.find((user) => String(user.userId) === String(id));
    if (!info) return;
    setInitialFormData({
      ...info,
      mobileNumber: info?.userMobileNumber || "",
      password: "",
      confirmPassword: "",
      inactiveReason: parseInactiveReasonFromInfo(info?.info),
    });
  }, [appConstants, allUsers, id]);

  useEffect(() => {
    setInactiveReasonExpanded(false);
  }, [id, initialFormData.inactiveReason]);

  const dismissAccountCreatedNotify = () => {
    setAccountCreatedNotify(null);
    navigate(location.pathname, { replace: true, state: {} });
  };

  useEffect(() => {
    if (id) return;
    const req = state?.registrationRequest;
    if (!req) return;

    const prefilled = {
      name: req?.name || "",
      email: req?.email || "",
      mobileNumber: req?.mobileNumber || "",
      department: req?.departmentId
        ? {
            id: req.departmentId,
            name: req?.departmentName || "",
            label: req?.departmentName || "",
            value: req?.departmentName || "",
          }
        : "",
      userRole: "",
      userName: req?.employeeId || "",
      password: "",
      confirmPassword: "",
      enabled: true,
      resetPassword: true,
    };
    setInitialFormData(prefilled);
    setEnableEditing(true);
  }, [id, state]);

  const isMaintenanceSupervisorRole =
    initialFormData.userRole === appConstants?.allTypes?.USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR ||
    initialFormData.userRole === appConstants?.allTypes?.CALMAN_USER_ROLES?.ROLE_MAINTENANCE_SUPERVISOR;
  const showFacilitiesTicketsChip =
    isMaintenanceSupervisorRole && parseResponsibleForFacilitiesTicketsFromInfo(initialFormData.info);

  const formatDisplayValue = (value) => {
    if (value == null || value === "") return "";
    if (typeof value === "object") {
      return value.name ?? value.label ?? value.value ?? "";
    }
    return String(value);
  };

  const renderLabel = (label, value) => (
    <Grid container spacing={1} mb={0.5} alignItems="center" wrap="nowrap">
      <Grid item sx={{ minWidth: 105 }}>
        <Typography fontWeight="bold" whiteSpace="nowrap">
          {label}
        </Typography>
      </Grid>
      <Grid item sx={{ minWidth: 20 }}>
        <Typography fontWeight="bold">:</Typography>
      </Grid>
      <Grid item xs>
        <Typography
          sx={{
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}
        >
          {formatDisplayValue(value)}
        </Typography>
      </Grid>
    </Grid>
  );

  const renderInactiveReasonLabel = (reason) => {
    const text = formatDisplayValue(reason).trim();
    if (!text) return null;

    const needsToggle = text.length > INACTIVE_REASON_PREVIEW_LENGTH;
    const displayText = needsToggle && !inactiveReasonExpanded ? `${text.slice(0, INACTIVE_REASON_PREVIEW_LENGTH)}…` : text;

    return (
      <Grid container spacing={1} mb={0.5} alignItems="flex-start" wrap="nowrap">
        <Grid item sx={{ minWidth: 105 }}>
          <Typography fontWeight="bold" whiteSpace="nowrap">
            Inactive reason
          </Typography>
        </Grid>
        <Grid item sx={{ minWidth: 20 }}>
          <Typography fontWeight="bold">:</Typography>
        </Grid>
        <Grid item xs>
          <Typography sx={{ wordBreak: "break-word", whiteSpace: "normal" }}>{displayText}</Typography>
          {needsToggle ? (
            <Button
              size="small"
              variant="text"
              disableRipple
              onClick={() => setInactiveReasonExpanded((prev) => !prev)}
              sx={{
                mt: 0.25,
                p: 0,
                minWidth: 0,
                textTransform: "none",
                fontSize: "0.8125rem",
                color: "primary.main",
                "&:hover": {
                  backgroundColor: "transparent",
                  color: "primary.main",
                },
              }}
            >
              {inactiveReasonExpanded ? "Show less" : "Show more"}
            </Button>
          ) : null}
        </Grid>
      </Grid>
    );
  };

  const dateComparator = (v1, v2) => moment(v1).diff(moment(v2));
  const gridTicketEdit = (args) => {
    return (
      <div>
        {user.roles[0] === appConstants?.allTypes?.USER_ROLES.ROLE_MAINTENANCE_SUPERVISOR ? (
          <Link to={`/${Links.SERVICE_TICKET}/` + args.row.ticketId}>
            <IconButton sx={{ mr: 1 }}>
              <Tooltip title="View">
                <ViewIcon sx={{ color: theme.palette.primary[50] }} />
              </Tooltip>
            </IconButton>
          </Link>
        ) : null}
      </div>
    );
  };
  const serviceTicketForMachineTableAttributes = [
    {
      field: "serviceTicketNumber",
      headerName: "Ticket ID",
      minWidth: 60,
      flex: 0.3,
      valueGetter: (params) => {
        return appConstants?.showServiceTicketId ? params.row.ticketId : params.row.serviceTicketNumber;
      },
      renderCell: (params) => {
        const displayValue = appConstants?.showServiceTicketId ? params.row.ticketId : params.value;
        return (
          <span
            style={{
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate(`/${Links.SERVICE_TICKET}/` + params.row.ticketId)}
          >
            {displayValue}
          </span>
        );
      },
    },
    {
      field: "dateCreated",
      headerName: "Created",
      minWidth: 60,
      flex: 0.3,
      sortComparator: dateComparator,
    },
    {
      field: "ticketCategory",
      headerName: "Category",
      minWidth: 60,
      flex: 0.3,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 60,
      flex: 0.3,
    },
    // {
    //   field: "edit",
    //   headerName: "",
    //   minWidth: 60,
    //   flex: 0.3,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: gridTicketEdit,
    // },
  ];

  return (
    <div>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header titles={["User", id ? `${initialFormData.name}  [${initialFormData.userName}]` : ""]} />
      </Stack>
      <Box sx={{ mx: 2 }}>
        {showEditForm ? (
          <Paper sx={{ borderRadius: 2, mb: 1.5, p: 2 }}>
            <AddEditUser
              initialFormData={initialFormData}
              setEnableEditing={setEnableEditing}
              registrationRequestUuid={state?.registrationRequestUuid}
            />
          </Paper>
        ) : (
          <Paper sx={{ borderRadius: 2, mb: 1.5, p: 3, position: "relative" }}>
            {showFacilitiesTicketsChip ? (
              <Chip
                label="Responsible for facility tickets"
                size="small"
                // color="primary"
                variant="contained"
                sx={{ position: "absolute", color: "#ffffff", backgroundColor: "#00b609", top: 12, right: 12 }}
              />
            ) : null}
            <Stack justifyContent={"space-between"} direction={"row"} flexWrap={"wrap"}>
              <Stack rowGap={0.5} sx={{ minWidth: 300, flex: 1 }}>
                {renderLabel("Name ", initialFormData.name)}
                {renderLabel("Username ", initialFormData.userName)}
                {renderLabel("Role ", initialFormData.userRole)}
                {renderLabel("Active ", initialFormData.enabled ? "Yes" : "No")}
                {!initialFormData.enabled && initialFormData.inactiveReason
                  ? renderInactiveReasonLabel(initialFormData.inactiveReason)
                  : null}
              </Stack>
              <Stack rowGap={0.5} sx={{ minWidth: 300, flex: 1 }}>
                {renderLabel("Email ", initialFormData.email)}
                {renderLabel("Mobile number ", formatPhoneNumber(initialFormData.mobileNumber))}
                {renderLabel("Department ", initialFormData.department)}
              </Stack>
              <Stack rowGap={1} sx={{ pt: showFacilitiesTicketsChip ? 3.5 : 0 }}>
                <Tooltip title="Edit">
                  <Button onClick={() => setEnableEditing(true)} variant="contained">
                    Edit
                  </Button>
                </Tooltip>
              </Stack>
            </Stack>
          </Paper>
        )}
        {id ? (
          <Paper sx={{ p: 2 }}>
            <Stack direction={"row"} alignItems={"center"} mb={2}>
              <Typography variant="h5" fontWeight={600}>
                Tickets created after :
              </Typography>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  maxDate={moment(new Date())}
                  label="Start date"
                  format={"DD MMM YYYY"}
                  value={selectedStartDate ? moment(selectedStartDate) : null}
                  onChange={(newValue) => setSelectedStartDate(newValue)}
                  sx={{ minWidth: 200, maxWidth: 600, ml: 2 }}
                />
              </LocalizationProvider>
            </Stack>
            {serviceTicketsByUserId?.isLoading ? (
              <TableSkeleton rows={5} columns={serviceTicketForMachineTableAttributes.length} />
            ) : (
              <DataGridTable rowId={"ticketId"} data={serviceTickets} columnAttributes={serviceTicketForMachineTableAttributes} />
            )}
          </Paper>
        ) : null}
      </Box>

      <RegistrationNotifyDialog
        open={!!accountCreatedNotify}
        onClose={dismissAccountCreatedNotify}
        onDone={dismissAccountCreatedNotify}
        title="Account created"
        subtitle="Share login credentials with the new user."
        statusMessage="The user account has been created successfully."
        variant="success"
        recipientName={accountCreatedNotify?.recipientName}
        recipientPhone={accountCreatedNotify?.mobileNumber}
        message={accountCreatedNotify?.message}
        doneLabel="Close"
      />
    </div>
  );
};

export default User;
